<template>
  <div>
    <v-dialog v-if="isAccountView" v-model="visible" max-width="376" eage persistent @keydown.esc="dismiss(true)">
      <component
        :is="mapComponent(mfaMethod)"
        :isParentVisible="visible"
        @sms-auth-complete="updateMfaDetails('sms')"
        @app-auth-completed="updateMfaDetails('auth_app')"
        @email-auth-complete="updateMfaDetails('email')"
        @mfa-verified="$emit('mfa-verified', $event)"
        @dismiss="dismiss(true)"
      />
    </v-dialog>
    <div v-if="isMFaView">
      <component
        :is="mapComponent(mfaMethod)"
        :hasMfaMethods="hasMfaMethods"
        :isParentVisible="visible"
        @sms-auth-complete="updateMfaDetails('sms')"
        @app-auth-completed="updateMfaDetails('auth_app')"
        @email-auth-complete="updateMfaDetails('email')"
        @mfa-verified="$emit('mfa-verified', $event)"
        @dismiss="dismiss(true)"
      />
      <!-- Redirect to login -->
      <p class="text-center mb-8">
        <a class="primary--text text-decoration-underline" @click.prevent="returnToLogin">
          Return to Login
        </a>
      </p>
      <p class="text-center bg-white">
        Contact
        <span class="primary--text text-decoration-underline cursor-pointer" @click="showContactUsModal = true">
          {{ $t("fields.supportOnviv") }}</span>
        if you should need assistance.
      </p>
      <contact-us-dlg v-model="showContactUsModal" @dimiss-contact-us="showContactUsModal = false"></contact-us-dlg>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import Vue from "vue";

import { ConfirmDialogOptions } from "@/plugins/confirm";

import { mfaTypeEnum } from "@/service";

import SmsAuthentication from "@/components/MFA/SmsAuthentication.vue";
import AppAuthentication from "@/components/MFA/AppAuthentication.vue";
import EmailAuthentication from "@/components/MFA/EmailAuthentication.vue";
import ContactUsDlg from "../ContactUsDlg.vue";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapMutations, mapActions, mapGetters } = createHelpers<RootStoreInterface>();

const setters = mapMutations(["addMfaAvail", "setMfaComplete", "setRedirectRoute","setLoginSecurityConfirmDlg"]);
const actions = mapActions(["updateMfaAvail","updateMFAVerification","resetCredentials","initialize"]);
const getters = mapGetters(["getMfaAvail", "redirectRoute", "user"]);

export default Vue.extend({
  components: {
    SmsAuthentication,
    AppAuthentication,
    EmailAuthentication,
    ContactUsDlg,
  },
  data: () => ({
    loading: false,
    showContactUsModal: false,
  }),
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    mfaMethod: {
      type: String as PropType<mfaTypeEnum>,
      required: true,
    },
    hasMfaMethods: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    visible: {
      get(): boolean {
        return this.value;
      },
      set(val: boolean) {
        this.$emit("input", val);
      },
    },
    isMFaView(): boolean {
      return this.$route.path === "/mfa" || this.$route.name === "mfaComponent";
    },
    isAccountView(): boolean {
      return this.$route.path === "/account" || this.$route.name === "Account"
    },
  },
  methods: {
    ...setters,
    ...actions,
    async save() {
      try {
        this.loading = true;
      } catch (e) {
        console.log("error completing authentication:", e);
      }
      this.loading = false;
    },
    dismiss(val: boolean = true) {
      if (val) {
        this.onCancelConfirm();
      } else {
        this.visible = false;
        if(this.isAccountView) {
          this.$nextTick(() => this.$eventBus.$emit("showLoginSecurityDlg"))
        }
        this.$emit("dismiss");
      }
    },
    mapComponent(action: mfaTypeEnum): string {
      const comp = action === mfaTypeEnum.AUTH_APP
        ? "AppAuthentication"
        : action === mfaTypeEnum.SMS
          ? "SmsAuthentication"
          : action === mfaTypeEnum.EMAIL
            ? "EmailAuthentication"
            : "";
      return comp;
    },
    onMfaVerified(payload: any) {
      this.$emit("mfa-verified", payload);
    },
    async onCancelConfirm() {
      // Show unsaved changes alert
      const options: ConfirmDialogOptions = {
        title: this.$t("headers.unsavedChanges") as string,
        buttonTrueText: this.$t("captions.stayOnPage") as string,
        buttonFalseText: this.$t("captions.discardChanges") as string,
      };
      const res = await this.$confirm(this.$t("messages.unsavedChanges") as string, options);
      if (!res) {
        this.dismiss(false);
      }
    },
    updateMfaDetails(value: mfaTypeEnum | string) {
      const validMfaTypes = [mfaTypeEnum.SMS, mfaTypeEnum.AUTH_APP, mfaTypeEnum.EMAIL];
      const mfaExits = this.getMfaAvail.includes(value as mfaTypeEnum);
      this.updateMFAVerification(true);


      if (validMfaTypes.includes(value as mfaTypeEnum) && !mfaExits) {
        this.addMfaAvail(value as mfaTypeEnum);
        this.updateMfaAvail({ mfaVal: value as mfaTypeEnum });
      }

      if(!this.isMFaView) this.setLoginSecurityConfirmDlg(false);

      this.$nextTick(() => {
        this.dismiss(false);
      });

      const homeRoute = '/';
      // goto User Dashboard after successful login and MFA
      if (this.isMFaView) {
        this.setMfaComplete(true);
        this.$nextTick(() => {
          if (this.redirectRoute) {
            this.redirectToLastLoginView();
          } else {
            // Safe fallback to home route with error suppression
            this.$router.replace(homeRoute).catch(() => { });
          }
        })
      }
    },
    redirectToLastLoginView() {
      const redirectRoute = this.redirectRoute;

      if (!redirectRoute) {
        this.$router.replace('/').catch(() => { });
        return;
      }

      const route = redirectRoute['guest'] || redirectRoute[`${this.user?.id}`] || null;

      if (!route || (!route.name && !route.path)) {
        this.$router.replace('/').catch(() => { });
        return;
      }

      const isMfaRedirection = route.path === '/mfa' || route.name === 'mfaComponent';

      if (route.name && !isMfaRedirection) {
        this.$router.replace({name: route.name,path: route.path, params: route.params, query: route.query,hash: route.hash,}).catch(() => {});

        this.setRedirectRoute(null);
      } else {
        this.$router.replace('/').catch(() => { });
      }
    }
    ,
    async returnToLogin() {
      try {
        await this.resetCredentials();
        await this.initialize();
        await this.$router.replace({ name: "Login" });
      } catch (e) {
        console.error("Return to login failed", e);
      }
    },
  },
  watch: {
    visible(val) {
      if (val) this.$eventBus.$emit("hideLoginSecurityDlg");
    },
  },
  mounted() {
    if (this.isMFaView) this.$eventBus.$emit("showAppBarBtn");
  },
});
</script>
<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
